// ContextPanel (web feature wrapper) — US-080..US-083 (Wave 5c Agent 5c.3).
//
// Wraps the DS-D `ContextPanel` composite (`@barel/ui`) and surfaces:
//
//   - Localised section labels + AI-mode descriptive copy (FR/EN via
//     `@barel/i18n`). The DS composite stays presentation-only; this
//     wrapper owns the locale + i18n key plumbing.
//   - Quick-action wiring:
//       * "Reply manually" (US-081) → opens `ManualReplyComposer`.
//       * "Reassign source" (US-082) → opens a controlled
//         `ReassignSourceMenu` anchored to the panel rail.
//
// Mounting status: per recon §6 the DS composite was shipped with
// `chromatic.disable: true` because it had no host route. **This wrapper
// makes it real** — once 5c.1's `InboxPage` composes it inside the
// thread-rail layout the panel renders for the selected conversation. We
// keep the DS scenarios' chromatic flag flip as a DS Wave H integrator
// task per the agent prompt.
//
// Coordination: 5c.1 / 5c.2 own the InboxPage + ThreadPanel; this
// component is mounted via the `<ContextPanel conv={selected} ... />` slot
// and re-renders whenever the selected conversation id changes.
//
// PostHog/Sentry no-op (Wave 5c hard rule).

import { useState } from 'react';

import { useReassignSource, type ConversationSourcePill } from '@barel/api-client/react';
import { SOURCE_PILLS } from '@barel/core';
import { useT } from '@barel/i18n';
import {
  ContextPanel as DSContextPanel,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  toast,
  type ContextPanelData,
  type ContextPanelLabels,
  type ContextPanelProps as DSContextPanelProps,
} from '@barel/ui';

import { ManualReplyComposer } from './ManualReplyComposer';

export interface ContextPanelProps {
  /**
   * Selected conversation context. Pass `null` when no conversation is
   * selected — the panel returns `null` and lets the parent decide the
   * empty-state copy.
   */
  conv: ContextPanelData | null;
  /** `onClose` shows a × in the panel header (typically only on mobile). */
  onClose?: DSContextPanelProps['onClose'];
  /** Hook into the AI-settings link (e.g., navigate to `/voice/:accountId`). */
  onNavSettings?: DSContextPanelProps['onNavSettings'];
  /** Width override (DS default: 240). */
  width?: DSContextPanelProps['width'];
}

/**
 * Resolve i18n keys + per-mode descriptive copy into a `ContextPanelLabels`
 * payload the DS composite expects. Keys land in `inbox.context.*` —
 * see `packages/i18n/src/locales/{fr,en}/inbox.json`.
 */
function useContextPanelLabels(conv: ContextPanelData): ContextPanelLabels {
  const t = useT('inbox');

  const aiModeLabel =
    conv.aiMode === 'auto'
      ? t('context.aiModeAutoLabel')
      : conv.aiMode === 'review'
        ? t('context.aiModeReviewLabel')
        : '';
  const aiModeDescription =
    conv.aiMode === 'auto'
      ? t('context.aiModeAutoDescription')
      : conv.aiMode === 'review'
        ? t('context.aiModeReviewDescription')
        : '';

  return {
    ariaLabel: t('context.ariaLabel'),
    title: t('context.title'),
    closeLabel: t('context.closeLabel'),
    sections: {
      account: t('context.sections.account'),
      business: t('context.sections.business'),
      campaign: t('context.sections.campaign'),
      source: t('context.sections.source'),
      aiMode: t('context.sections.aiMode'),
      quickActions: t('context.sections.quickActions'),
    },
    noCampaignPlaceholder: t('context.noCampaignPlaceholder'),
    sourceAutoDetected: t('context.sourceAutoDetected'),
    sourceManual: t('context.sourceManual'),
    sourceLabel: t(`context.reassign.options.${conv.source}`),
    aiModeDescription,
    aiModeLabel,
    aiSettingsLink: t('context.aiSettingsLink'),
    quickActions: {
      replyManually: t('context.replyManually'),
      reassignSource: t('context.reassignSource'),
    },
  };
}

/** Stable placeholder used to keep hook order consistent when `conv` is null. */
const PLACEHOLDER_CONV: ContextPanelData = {
  id: '__none__',
  name: '',
  title: '',
  company: '',
  initials: '',
  accountColor: 'var(--accent-default)',
  accountId: '',
  accountLabel: '',
  accountSubtitle: '',
  business: '',
  businessDescription: '',
  source: 'Unknown',
  sourceAutoDetected: true,
  aiMode: 'auto',
};

export function ContextPanel({ conv, onClose, onNavSettings, width }: ContextPanelProps) {
  const t = useT('inbox');
  const reassign = useReassignSource();

  const [composerOpen, setComposerOpen] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);

  // Hooks must run unconditionally — we always invoke `useContextPanelLabels`
  // with a placeholder when `conv === null`, then bail before render.
  const safeConv: ContextPanelData = conv ?? PLACEHOLDER_CONV;
  const labels = useContextPanelLabels(safeConv);

  if (conv === null) return null;

  const handleReassign = async (next: ConversationSourcePill): Promise<void> => {
    setReassignOpen(false);
    if (next === conv.source) return;
    try {
      await reassign.mutateAsync({ conversationId: conv.id, sourcePill: next });
      toast.success({ title: t('context.reassign.successToast') });
    } catch {
      toast.error({ title: t('context.reassign.errorToast') });
    }
  };

  return (
    <div data-testid="inbox-context-panel-root" data-conv-id={conv.id}>
      <DSContextPanel
        conv={conv}
        labels={labels}
        onClose={onClose}
        onNavSettings={onNavSettings}
        onReplyManually={() => setComposerOpen(true)}
        onReassignSource={() => setReassignOpen(true)}
        width={width}
      />

      {/* Controlled DropdownMenu — opened imperatively by the DS composite's
          quick-action button. The hidden trigger anchors the floating menu
          to the panel rail; visually-hidden but tabbable so screen-reader
          users can also reach the dropdown. */}
      <DropdownMenu open={reassignOpen} onOpenChange={setReassignOpen}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="sr-only"
            data-testid="reassign-source-anchor"
            aria-label={t('context.reassignSource')}
          >
            {t('context.reassignSource')}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" data-testid="reassign-source-menu">
          <DropdownMenuLabel>{t('context.reassign.menuLabel')}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {SOURCE_PILLS.map((pill) => (
            <DropdownMenuItem
              key={pill}
              onSelect={() => {
                void handleReassign(pill);
              }}
              data-testid={`reassign-source-${pill}`}
              data-current={pill === conv.source || undefined}
            >
              {t(`context.reassign.options.${pill}`)}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <ManualReplyComposer
        open={composerOpen}
        onClose={() => setComposerOpen(false)}
        conversationId={conv.id}
      />
    </div>
  );
}
